import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Flame } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/**
 * FlameIndicator — animated flame reflecting the user's login streak + multiplier tier.
 * Uses real flickering CSS animation (not just pulse) with per-tier intensity.
 *
 * Usage:
 *   <FlameIndicator variant="inline" />
 *   <FlameIndicator variant="card" />
 */
const TIER_STYLES = {
  none: { size: 14, color: 'text-muted-foreground', bg: 'bg-muted/30', glow: '', anim: 'flame-flicker-lite' },
  etincelle: { size: 18, color: 'text-orange-400', bg: 'bg-orange-100/70', glow: 'flame-glow-sm', anim: 'flame-flicker-lite' },
  flamme: { size: 22, color: 'text-orange-500', bg: 'bg-gradient-to-br from-orange-100 to-amber-200', glow: 'flame-glow-md', anim: 'flame-flicker' },
  flamme_doree: { size: 26, color: 'text-yellow-500', bg: 'bg-gradient-to-br from-yellow-100 to-orange-200', glow: 'flame-glow-lg', anim: 'flame-flicker-strong' },
  brasier: { size: 30, color: 'text-red-500', bg: 'bg-gradient-to-br from-red-100 via-orange-200 to-yellow-100', glow: 'flame-glow-xl', anim: 'flame-flicker-strong' },
  legend: { size: 34, color: 'text-purple-600', bg: 'bg-gradient-to-br from-purple-100 via-red-200 to-orange-200', glow: 'flame-glow-legend', anim: 'flame-flicker-legend' },
};

export const useStreak = () => {
  const { api, user } = useAuth();
  const [streak, setStreak] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchStreak = async () => {
      try {
        const res = await api.get('/gamification/streak');
        if (!cancelled) setStreak(res.data);
      } catch (e) { /* silent */ }
    };
    fetchStreak();
    const interval = setInterval(fetchStreak, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [api, user]);

  return streak;
};

const FlameIndicator = ({ variant = 'inline', streak: streakProp = null }) => {
  const fetchedStreak = useStreak();
  const streak = streakProp || fetchedStreak;

  if (!streak) return null;
  if (streak.current_streak < 1) return null;

  const tier = streak.tier || 'etincelle';
  const style = TIER_STYLES[tier] || TIER_STYLES.etincelle;

  if (variant === 'card') {
    // Big dedicated card, only shown for streak >= 7
    if (streak.current_streak < 7) return null;
    return (
      <div
        className={`relative rounded-xl border-2 border-primary/20 p-4 ${style.bg} overflow-hidden`}
        data-testid="streak-card"
      >
        <div className="absolute -right-3 -top-3 opacity-20">
          <Flame className={`w-24 h-24 ${style.color}`} strokeWidth={1.5} />
        </div>
        <div className="relative flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-white/80 ${style.glow}`}>
            <Flame
              className={`${style.color} ${style.anim}`}
              style={{ width: style.size + 6, height: style.size + 6, transformOrigin: 'center bottom' }}
              strokeWidth={2}
              fill="currentColor"
              fillOpacity={0.25}
            />
          </div>
          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-foreground">{streak.current_streak}</span>
              <span className="text-sm text-muted-foreground">jours d&apos;affilée</span>
            </div>
            <p className="text-xs font-semibold text-primary mt-1">{streak.tier_label}</p>
            {streak.eligible ? (
              <p className="text-xs text-foreground/70 mt-2">
                🚀 Multiplicateur <b>×{streak.xp_mult}</b> XP actif · plafond <b>×{streak.cap_mult}</b>
              </p>
            ) : streak.current_streak >= 7 ? (
              <p className="text-xs text-muted-foreground mt-2">
                Valide le défi du jour {!streak.daily_done && '❌'} et un défi hebdo {!streak.weekly_done && '❌'} pour activer le ×{streak.raw_tier_xp_mult}
              </p>
            ) : null}
            {streak.next_tier_days && (
              <p className="text-[10px] text-muted-foreground mt-2 italic">
                Plus que {streak.next_tier_days} j pour le prochain palier
              </p>
            )}
          </div>
        </div>
        <FlameStyles />
      </div>
    );
  }

  // Inline variant: small flame with tooltip
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${style.bg} ${style.glow} cursor-help`}
            data-testid="flame-indicator"
          >
            <Flame
              className={`${style.color} ${style.anim}`}
              style={{ width: style.size, height: style.size, transformOrigin: 'center bottom' }}
              strokeWidth={2.5}
              fill="currentColor"
              fillOpacity={0.3}
            />
            <span className={`text-xs font-bold ${style.color}`}>{streak.current_streak}j</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1 text-xs">
            <p className="font-semibold text-sm">{streak.tier_label} · {streak.current_streak} jours</p>
            {streak.eligible ? (
              <p className="text-green-600 dark:text-green-400">
                ✨ Multiplicateur ×{streak.xp_mult} XP actif !
              </p>
            ) : streak.current_streak >= 7 ? (
              <p className="text-orange-600">
                Complète le défi du jour + un défi hebdo pour activer le ×{streak.raw_tier_xp_mult}
              </p>
            ) : (
              <p>Continue à te connecter chaque jour pour faire grandir la flamme !</p>
            )}
            {streak.next_tier_days && (
              <p className="text-muted-foreground">
                Prochain palier dans {streak.next_tier_days} j
              </p>
            )}
            <p className="text-[10px] text-muted-foreground pt-1 border-t">
              Record : {streak.longest_streak} j
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
      <FlameStyles />
    </TooltipProvider>
  );
};

// Inline styles for flame flicker animations (scoped keyframes)
const FlameStyles = () => (
  <style>{`
    @keyframes flame-flicker-lite {
      0%, 100% { transform: scale(1) rotate(-1deg); opacity: 0.95; }
      50%      { transform: scale(1.08) rotate(1deg); opacity: 1; }
    }
    @keyframes flame-flicker {
      0%   { transform: scale(1) rotate(-2deg) translateY(0); opacity: 0.9; }
      25%  { transform: scale(1.12) rotate(2deg)  translateY(-1px); opacity: 1; }
      50%  { transform: scale(0.96) rotate(-1deg) translateY(0); opacity: 0.85; }
      75%  { transform: scale(1.10) rotate(3deg)  translateY(-1px); opacity: 1; }
      100% { transform: scale(1) rotate(-2deg) translateY(0); opacity: 0.9; }
    }
    @keyframes flame-flicker-strong {
      0%   { transform: scale(1)    rotate(-4deg) translateY(0)   skewX(-2deg); opacity: 0.9;  filter: drop-shadow(0 0 4px currentColor); }
      20%  { transform: scale(1.18) rotate(3deg)  translateY(-2px) skewX(2deg);  opacity: 1;    filter: drop-shadow(0 0 8px currentColor); }
      40%  { transform: scale(0.94) rotate(-2deg) translateY(1px) skewX(-1deg); opacity: 0.85; filter: drop-shadow(0 0 3px currentColor); }
      60%  { transform: scale(1.14) rotate(4deg)  translateY(-2px) skewX(2deg);  opacity: 1;    filter: drop-shadow(0 0 10px currentColor); }
      80%  { transform: scale(1.02) rotate(-3deg) translateY(0)   skewX(-2deg); opacity: 0.95; filter: drop-shadow(0 0 5px currentColor); }
      100% { transform: scale(1)    rotate(-4deg) translateY(0)   skewX(-2deg); opacity: 0.9;  filter: drop-shadow(0 0 4px currentColor); }
    }
    @keyframes flame-flicker-legend {
      0%   { transform: scale(1)    rotate(-5deg) translateY(0)   skewX(-3deg); filter: drop-shadow(0 0 6px #a855f7) hue-rotate(0deg); }
      25%  { transform: scale(1.22) rotate(4deg)  translateY(-3px) skewX(3deg);  filter: drop-shadow(0 0 12px #ef4444) hue-rotate(15deg); }
      50%  { transform: scale(0.92) rotate(-3deg) translateY(2px) skewX(-2deg); filter: drop-shadow(0 0 4px #f97316) hue-rotate(-10deg); }
      75%  { transform: scale(1.18) rotate(5deg)  translateY(-3px) skewX(3deg);  filter: drop-shadow(0 0 14px #a855f7) hue-rotate(20deg); }
      100% { transform: scale(1)    rotate(-5deg) translateY(0)   skewX(-3deg); filter: drop-shadow(0 0 6px #a855f7) hue-rotate(0deg); }
    }
    .flame-flicker-lite    { animation: flame-flicker-lite    1.6s ease-in-out infinite; }
    .flame-flicker         { animation: flame-flicker         1.1s ease-in-out infinite; }
    .flame-flicker-strong  { animation: flame-flicker-strong  0.9s ease-in-out infinite; }
    .flame-flicker-legend  { animation: flame-flicker-legend  0.85s ease-in-out infinite; }
    .flame-glow-sm { box-shadow: 0 0 8px rgba(251, 146, 60, 0.35); }
    .flame-glow-md { box-shadow: 0 0 12px rgba(251, 146, 60, 0.55); }
    .flame-glow-lg { box-shadow: 0 0 18px rgba(234, 179, 8, 0.6); }
    .flame-glow-xl { box-shadow: 0 0 24px rgba(239, 68, 68, 0.6); }
    .flame-glow-legend { box-shadow: 0 0 28px rgba(168, 85, 247, 0.7), 0 0 40px rgba(239, 68, 68, 0.4); }
  `}</style>
);

export default FlameIndicator;
